import ApiClient from '@/utils/ApiClient';
import { SubscriptionStatus, SubscriptionType } from '@/constant';
import { BillingCycle } from '@/types/subscription-management.types';
import {
  ISubscription,
  ISubscriptionsListResponse,
  SubscriptionPayload,
} from '@/types/subscription-management.types';
import type { ISubscriptionUsersListResponse } from '@/types/subscription-management.types';

type SubscriptionEntityQueryParams = {
  harborId?: string;
  clubId?: string;
  clubRegistrationRequestId?: string;
  serviceProviderId?: string;
};

const getRequiredHeaders = () => {
  return {
    'x-lang': 'en',
    'x-session-id': localStorage.getItem('sessionId') || '',
    'x-location-name': localStorage.getItem('locationName') || '',
    'x-latitude': localStorage.getItem('latitude') || '',
    'x-longitude': localStorage.getItem('longitude') || '',
    is_admin_panel: 'true',
  };
};

export const SubscriptionsService = {
  getSubscriptions: async (
    page = 1,
    pageSize = 10,
    search?: string,
    statusFilter?: number,
    subscriptionType?: number
  ): Promise<ISubscriptionsListResponse> => {
    const params: {
      page: number;
      limit: number;
      search?: string;
      status?: number;
      subscription_type?: number;
    } = {
      page,
      limit: pageSize,
    };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (statusFilter !== undefined && statusFilter !== null) {
      params.status = statusFilter;
    }
    if (subscriptionType !== undefined && subscriptionType !== null) {
      params.subscription_type = subscriptionType;
    }

    const res = await ApiClient.get('/subscriptions', {
      params,
      headers: getRequiredHeaders(),
    });

    // Normalize a variety of possible response shapes
    const normalizeItem = (raw: unknown): ISubscription => {
      const item = (raw ?? {}) as Record<string, unknown>;
      const id = (item.id as string) ?? (item._id as string) ?? '';
      const name = (item.name as string) ?? '';
      const name_sub_title =
        (item.name_sub_title as string) ??
        (item['nameSubTitle'] as string) ??
        '';
      const description = (item.description as string) ?? '';
      const priceVal = item.price as number | string | undefined;
      const price =
        typeof priceVal === 'number'
          ? priceVal
          : priceVal !== undefined
            ? Number(priceVal)
            : undefined;
      // Normalize billing_cycle to a number (1: MONTHLY, 2: YEARLY)
      let billing_cycle: number | undefined = undefined;
      const bcRaw =
        (item.billing_cycle as unknown) ?? (item.billingCycle as unknown);
      if (typeof bcRaw === 'number') {
        billing_cycle = bcRaw;
      } else if (typeof bcRaw === 'string') {
        const parsed = Number(bcRaw);
        if (!Number.isNaN(parsed)) {
          billing_cycle = parsed;
        } else {
          const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
          if (key in BillingCycle) {
            billing_cycle = BillingCycle[key] as number;
          }
        }
      }
      const statusRaw = item.status as unknown;
      let status: number | undefined;
      if (typeof statusRaw === 'number') {
        status = statusRaw;
      } else if (typeof statusRaw === 'string') {
        const key = statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
        status = SubscriptionStatus[key] as number | undefined;
      }
      const is_active_raw = item.is_active as
        | boolean
        | number
        | string
        | undefined;
      const is_active =
        typeof status === 'number'
          ? status === SubscriptionStatus.ACTIVE
          : typeof is_active_raw === 'boolean'
            ? is_active_raw
            : Boolean(is_active_raw ?? true);
      const updated_at =
        (item.updated_at as string | undefined) ||
        (item.updatedAt as string | undefined) ||
        new Date().toISOString();

      // subscription_type may come as number or string (e.g., "BOATER")
      const subTypeRaw =
        (item.subscription_type as unknown) ||
        (item.subscriptionType as unknown) ||
        (item.type as unknown);
      let subscription_type: number | undefined = undefined;
      if (typeof subTypeRaw === 'number') {
        subscription_type = subTypeRaw;
      } else if (typeof subTypeRaw === 'string') {
        const asNum = Number(subTypeRaw);
        if (!Number.isNaN(asNum)) {
          subscription_type = asNum;
        } else {
          const key = subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
          if (key in SubscriptionType) {
            subscription_type = SubscriptionType[key] as number;
          }
        }
      }

      // Pass through translations map if the API returns it
      const translationsRaw = item.translations as unknown;
      let translations:
        | ISubscription['translations']
        | undefined = undefined;
      if (
        translationsRaw &&
        typeof translationsRaw === 'object' &&
        !Array.isArray(translationsRaw)
      ) {
        translations = translationsRaw as ISubscription['translations'];
      } else if (typeof translationsRaw === 'string') {
        try {
          translations = JSON.parse(translationsRaw) as ISubscription['translations'];
        } catch {
          translations = undefined;
        }
      }

      const apple_product_id =
        (item.apple_product_id as string | null | undefined) ?? null;

      return {
        id,
        name,
        name_sub_title,
        description,
        price,
        billing_cycle,
        is_active,
        status,
        subscription_type,
        updated_at,
        translations,
        apple_product_id,
      };
    };

    const data = res.data as unknown;
    if (Array.isArray(data)) {
      return {
        subscriptions: data.map(normalizeItem),
        totalCount: data.length,
      };
    }

    const obj = (data ?? {}) as Record<string, unknown>;
    const status = obj.status as boolean | undefined;
    const inner = obj.data as unknown;
    if (status && inner) {
      if (Array.isArray(inner)) {
        return {
          subscriptions: inner.map(normalizeItem),
          totalCount: inner.length,
        };
      }
      const innerObj = (inner ?? {}) as Record<string, unknown>;
      const subs = innerObj.subscriptions as unknown[] | undefined;
      if (Array.isArray(subs)) {
        const totalCount =
          (innerObj.totalCount as number | undefined) ||
          (innerObj.totalItems as number | undefined) ||
          subs.length;
        return {
          subscriptions: subs.map(normalizeItem),
          totalCount,
        };
      }
    }

    // Fallback
    return {
      subscriptions: [],
      totalCount: 0,
    };
  },

  getSubscriptionById: async (id: string): Promise<ISubscription> => {
    const response = await ApiClient.get(`/subscriptions/${id}`, {
      headers: getRequiredHeaders(),
    });

    const raw = (response?.data?.data ?? response?.data ?? {}) as Record<
      string,
      unknown
    >;
    const statusRaw = raw.status as unknown;
    let status: number | undefined;
    if (typeof statusRaw === 'number') {
      status = statusRaw;
    } else if (typeof statusRaw === 'string') {
      const key = statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
      status = SubscriptionStatus[key] as number | undefined;
    }
    const is_active_raw = raw.is_active as
      | boolean
      | number
      | string
      | undefined;
    // Normalize billing_cycle to a number (1: MONTHLY, 2: YEARLY)
    let billing_cycle: number | undefined = undefined;
    const bcRaw =
      (raw.billing_cycle as unknown) ?? (raw.billingCycle as unknown);
    if (typeof bcRaw === 'number') {
      billing_cycle = bcRaw;
    } else if (typeof bcRaw === 'string') {
      const parsedNum = Number(bcRaw);
      if (!Number.isNaN(parsedNum)) {
        billing_cycle = parsedNum;
      } else {
        const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
        if (key in BillingCycle) {
          billing_cycle = BillingCycle[key] as number;
        }
      }
    }

    // Pass through translations map if the API returns it
    const translationsRaw = raw.translations as unknown;
    let translations: ISubscription['translations'] | undefined = undefined;
    if (
      translationsRaw &&
      typeof translationsRaw === 'object' &&
      !Array.isArray(translationsRaw)
    ) {
      translations = translationsRaw as ISubscription['translations'];
    } else if (typeof translationsRaw === 'string') {
      try {
        translations = JSON.parse(translationsRaw) as ISubscription['translations'];
      } catch {
        translations = undefined;
      }
    }

    const parsed: ISubscription = {
      id: (raw.id as string) ?? (raw._id as string) ?? id,
      name: (raw.name as string) ?? '',
      name_sub_title:
        (raw.name_sub_title as string) ??
        ((raw as Record<string, unknown>)['nameSubTitle'] as string) ??
        '',
      description: (raw.description as string) ?? '',
      price:
        typeof raw.price === 'number'
          ? (raw.price as number)
          : raw.price !== undefined
            ? Number(raw.price as string)
            : undefined,
      billing_cycle,
      is_active:
        typeof status === 'number'
          ? status === SubscriptionStatus.ACTIVE
          : typeof is_active_raw === 'boolean'
            ? (is_active_raw as boolean)
            : Boolean((is_active_raw as unknown) ?? true),
      status,
      subscription_type: (() => {
        const subTypeRaw =
          (raw.subscription_type as unknown) ||
          (raw.subscriptionType as unknown) ||
          (raw.type as unknown);
        if (typeof subTypeRaw === 'number') return subTypeRaw;
        if (typeof subTypeRaw === 'string') {
          const asNum = Number(subTypeRaw);
          if (!Number.isNaN(asNum)) return asNum;
          const key = subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
          if (key in SubscriptionType) return SubscriptionType[key] as number;
        }
        return undefined;
      })(),
      updated_at:
        (raw.updated_at as string) ??
        (raw.updatedAt as string) ??
        new Date().toISOString(),
      translations,
    };
    return parsed;
  },

  createSubscription: async (payload: SubscriptionPayload) => {
    const response = await ApiClient.post('/subscriptions', payload, {
      headers: getRequiredHeaders(),
    });
    return response.data;
  },

  updateSubscription: async (id: string, payload: SubscriptionPayload) => {
    const response = await ApiClient.put(`/subscriptions/${id}`, payload, {
      headers: getRequiredHeaders(),
    });
    return response.data;
  },

  deleteSubscription: async (id: string) => {
    const response = await ApiClient.delete(`/subscriptions/${id}`, {
      headers: getRequiredHeaders(),
    });
    return response.data;
  },

  // Fetch assigned permission IDs for a subscription
  getSubscriptionPermissionIds: async (id: string): Promise<string[]> => {
    const res = await ApiClient.get(`/subscriptions/${id}/permissions`, {
      headers: getRequiredHeaders(),
    });

    // Response may be { status, data: [] } or direct array
    const raw = (res?.data as unknown) ?? [];
    const data = Array.isArray(raw)
      ? raw
      : ((raw as { data?: unknown }).data ?? []);

    if (Array.isArray(data)) {
      // Allow strings or objects containing an id field
      return (data as unknown[])
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object' && 'id' in (item as object)) {
            return (item as { id: string }).id;
          }
          if (
            item &&
            typeof item === 'object' &&
            'permission_id' in (item as object)
          ) {
            return (item as { permission_id: string }).permission_id;
          }
          return null;
        })
        .filter((v): v is string => Boolean(v));
    }

    return [];
  },

  // Get all subscription capabilities (full list)
  getSubscriptionAllCapabilities: async (
    subscriptionId: string
  ): Promise<
    Array<{
      id: string;
      name: string;
      unique_key: string;
      include: boolean;
    }>
  > => {
    const res = await ApiClient.get(
      `/subscriptions/${subscriptionId}/all-capabilities`,
      {
        headers: getRequiredHeaders(),
      }
    );

    // Normalize response structure: { status, message, data: [...] }
    const raw = (res?.data as unknown) ?? {};
    const obj = (raw ?? {}) as Record<string, unknown>;

    // Handle { status, message, data: [...] } structure
    if (obj.status && obj.data) {
      const data = obj.data as unknown;
      if (Array.isArray(data)) {
        return data.map((item) => ({
          id: (item.id as string) ?? '',
          name: (item.name as string) ?? '',
          unique_key: (item.unique_key as string) ?? '',
          include: Boolean(item.include ?? false),
        }));
      }
    }

    // If data is directly an array
    if (Array.isArray(obj.data)) {
      return obj.data.map((item) => ({
        id: (item.id as string) ?? '',
        name: (item.name as string) ?? '',
        unique_key: (item.unique_key as string) ?? '',
        include: Boolean(item.include ?? false),
      }));
    }

    // If response is directly an array
    if (Array.isArray(raw)) {
      return raw.map((item) => ({
        id: (item.id as string) ?? '',
        name: (item.name as string) ?? '',
        unique_key: (item.unique_key as string) ?? '',
        include: Boolean(item.include ?? false),
      }));
    }

    return [];
  },

  // Get subscription capabilities (checked state)
  getSubscriptionCapabilities: async (
    subscriptionId: string,
    actorId?: string
  ): Promise<
    Array<{
      id: string;
      name: string;
      unique_key: string;
      include: boolean;
    }>
  > => {
    const params = actorId ? { actor_id: actorId } : undefined;
    const res = await ApiClient.get(
      `/subscriptions/${subscriptionId}/capabilities`,
      {
        headers: getRequiredHeaders(),
        params,
      }
    );

    // Normalize response structure: { status, message, data: [...] }
    const raw = (res?.data as unknown) ?? {};
    const obj = (raw ?? {}) as Record<string, unknown>;

    // Handle { status, message, data: [...] } structure
    if (obj.status && obj.data) {
      const data = obj.data as unknown;
      if (Array.isArray(data)) {
        return data.map((item) => ({
          id: (item.id as string) ?? '',
          name: (item.name as string) ?? '',
          unique_key: (item.unique_key as string) ?? '',
          include: Boolean(item.include ?? false),
        }));
      }
    }

    // If data is directly an array
    if (Array.isArray(obj.data)) {
      return obj.data.map((item) => ({
        id: (item.id as string) ?? '',
        name: (item.name as string) ?? '',
        unique_key: (item.unique_key as string) ?? '',
        include: Boolean(item.include ?? false),
      }));
    }

    // If response is directly an array
    if (Array.isArray(raw)) {
      return raw.map((item) => ({
        id: (item.id as string) ?? '',
        name: (item.name as string) ?? '',
        unique_key: (item.unique_key as string) ?? '',
        include: Boolean(item.include ?? false),
      }));
    }

    return [];
  },

  // Update assigned permissions for a subscription
  updateSubscriptionPermissions: async (
    id: string,
    permissionIds: string[] | null
  ) => {
    const response = await ApiClient.put(
      `/subscriptions/${id}/permissions`,
      { ids: permissionIds },
      { headers: getRequiredHeaders() }
    );
    return response.data;
  },

  // Translate subscription content (name/description/name_sub_title) similar to organizations
  // Payload should be JSON: { name?: string; description?: string; name_sub_title?: string; source_language: string }
  translateSubscriptionContent: async (payload: {
    name?: string;
    description?: string;
    name_sub_title?: string;
    source_language: string;
  }): Promise<
    Record<
      string,
      { name?: string; description?: string; name_sub_title?: string }
    > & { warning_message?: string }
  > => {
    const headers = {
      ...getRequiredHeaders(),
      'Content-Type': 'application/json',
      'x-lang': payload?.source_language || 'en',
    };
    const res = await ApiClient.post(`/subscriptions/translate`, payload, {
      headers,
    });

    // Normalize response: supports { status, data }, { translations }, or direct map
    const raw = (res?.data as unknown) ?? {};
    let normalized: Record<
      string,
      { name?: string; description?: string; name_sub_title?: string }
    > & { warning_message?: string } = {};

    if (raw && typeof raw === 'object') {
      const asObj = raw as Record<string, unknown>;
      if (asObj.status && asObj.data) {
        normalized =
          (asObj.data as Record<
            string,
            { name?: string; description?: string; name_sub_title?: string }
          >) ?? {};
      } else if (asObj.translations) {
        normalized =
          (asObj.translations as Record<
            string,
            { name?: string; description?: string; name_sub_title?: string }
          >) ?? {};
      } else {
        normalized =
          (raw as Record<
            string,
            { name?: string; description?: string; name_sub_title?: string }
          >) ?? {};
      }

      if ('warning_message' in asObj && typeof asObj.warning_message === 'string') {
        normalized.warning_message = asObj.warning_message;
      }
    }
    return normalized;
  },

  // Fetch users (optionally filtered by subscription plan) using query params
  getSubscriptionUsers: async (
    subscriptionId?: string,
    page = 1,
    pageSize = 10,
    search?: string
  ): Promise<ISubscriptionUsersListResponse> => {
    const params: {
      page: number;
      limit: number;
      search?: string;
      subscription_id?: string;
    } = {
      page,
      limit: pageSize,
    };
    if (search && search.trim()) params.search = search.trim();
    if (subscriptionId && subscriptionId.trim())
      params.subscription_id = subscriptionId.trim();

    const res = await ApiClient.get(`/subscriptions/users-with-subscriptions`, {
      params,
      headers: getRequiredHeaders(),
    });

    type Raw = Record<string, unknown>;
    const normalizeUser = (raw: unknown) => {
      const item = (raw ?? {}) as Raw;
      const id = (item.id as string) ?? (item._id as string) ?? '';
      const name = (item.name as string) ?? (item.full_name as string) ?? '';
      const email = (item.email as string) ?? (item.user_email as string) ?? '';
      const rolesArr = (item.roles as unknown) ?? (item.role as unknown);
      const roles: string[] | undefined = (() => {
        if (Array.isArray(rolesArr)) {
          const mapped = rolesArr
            .map((r) => {
              if (typeof r === 'string') return r;
              if (r && typeof r === 'object') {
                const obj = r as Record<string, unknown>;
                const val =
                  (obj.display_name as string | undefined) ||
                  (obj.name as string | undefined) ||
                  (obj.role as string | undefined) ||
                  (obj.key as string | undefined) ||
                  (obj.id as string | undefined);
                return val ? String(val) : undefined;
              }
              return undefined;
            })
            .filter((v): v is string => Boolean(v && v.trim()))
            .map((v) => v.trim());
          return mapped.length ? mapped : undefined;
        }
        if (rolesArr && typeof rolesArr === 'object') {
          const obj = rolesArr as Record<string, unknown>;
          const val =
            (obj.display_name as string | undefined) ||
            (obj.name as string | undefined) ||
            (obj.role as string | undefined) ||
            (obj.key as string | undefined) ||
            (obj.id as string | undefined);
          return val ? [String(val)] : undefined;
        }
        if (typeof rolesArr === 'string') return [rolesArr];
        return undefined;
      })();
      const statusRaw = item.status as unknown;
      const subscriptionObj =
        (item.subscription as Raw | undefined) ?? undefined;
      const userSubscriptionObj =
        (item.user_subscription as Raw | undefined) ?? undefined;
      const status =
        typeof statusRaw === 'number'
          ? statusRaw
          : typeof statusRaw === 'string'
            ? statusRaw
            : (userSubscriptionObj?.status as number | string | undefined);
      const profile_path =
        (item.profile_picture as string | undefined) ||
        (item.profile_path as string | undefined);
      const subscription_id =
        (subscriptionObj?.id as string | undefined) ||
        (item.subscription_id as string | undefined) ||
        subscriptionId;
      const subscription_name =
        (subscriptionObj?.name as string | undefined) ||
        (item.subscription_name as string | undefined) ||
        (item.subscription as string | undefined);
      const subscribed_at =
        (userSubscriptionObj?.starts_at as string | undefined) ||
        (item.subscribed_at as string | undefined) ||
        (item.created_at as string | undefined) ||
        (item.updated_at as string | undefined);

      return {
        id,
        name,
        email,
        roles,
        status,
        profile_path,
        subscription_id,
        subscription_name,
        subscribed_at,
      };
    };

    const data = res?.data as unknown;
    if (Array.isArray(data)) {
      return { users: data.map(normalizeUser), totalCount: data.length };
    }
    const obj = (data ?? {}) as Raw;
    if (obj.status && obj.data) {
      const inner = (obj.data as unknown) ?? [];
      if (Array.isArray(inner)) {
        return { users: inner.map(normalizeUser), totalCount: inner.length };
      }
      const innerObj = (inner ?? {}) as Raw;
      const list =
        (innerObj.users as unknown[]) || (innerObj.list as unknown[]) || [];
      const totalCount =
        (innerObj.totalCount as number | undefined) ||
        (innerObj.totalItems as number | undefined) ||
        list.length;
      return { users: list.map(normalizeUser), totalCount };
    }

    const users = ((obj as Raw).users as unknown[]) || [];
    if (Array.isArray(users)) {
      const totalCount =
        ((obj as Raw).totalCount as number | undefined) ||
        ((obj as Raw).totalItems as number | undefined) ||
        users.length;
      return { users: users.map(normalizeUser), totalCount };
    }

    return { users: [], totalCount: 0 };
  },

  // Subscribe or change the subscription plan for a particular user
  subscribeUser: async (userId: string, subscriptionId: string) => {
    const headers = {
      ...getRequiredHeaders(),
      'Content-Type': 'application/json',
    };
    const payload = { subscriptionId: subscriptionId };
    const res = await ApiClient.post(
      `/subscriptions/${userId}/subscribe`,
      payload,
      {
        headers,
      }
    );
    return res.data;
  },

  // Fetch subscriptions by type (club, harbor, etc.)
  getSubscriptionsByType: async (
    type: string,
    params: SubscriptionEntityQueryParams = {}
  ): Promise<ISubscriptionsListResponse> => {
    const queryParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value)
    );

    const res = await ApiClient.get(`/subscriptions/type/${type}`, {
      params: queryParams,
      headers: getRequiredHeaders(),
    });

    // Normalize a variety of possible response shapes
    const normalizeItem = (raw: unknown): ISubscription => {
      const item = (raw ?? {}) as Record<string, unknown>;
      const id = (item.id as string) ?? (item._id as string) ?? '';
      const name = (item.name as string) ?? '';
      const name_sub_title =
        (item.name_sub_title as string) ??
        (item['nameSubTitle'] as string) ??
        '';
      const description = (item.description as string) ?? '';
      const priceVal = item.price as number | string | undefined;
      const price =
        typeof priceVal === 'number'
          ? priceVal
          : priceVal !== undefined
            ? Number(priceVal)
            : undefined;

      // Normalize billing_cycle to a number (1: MONTHLY, 2: YEARLY)
      let billing_cycle: number | undefined = undefined;
      const bcRaw =
        (item.billing_cycle as unknown) ?? (item.billingCycle as unknown);
      if (typeof bcRaw === 'number') {
        billing_cycle = bcRaw;
      } else if (typeof bcRaw === 'string') {
        const parsed = Number(bcRaw);
        if (!Number.isNaN(parsed)) {
          billing_cycle = parsed;
        } else {
          const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
          if (key in BillingCycle) {
            billing_cycle = BillingCycle[key] as number;
          }
        }
      }

      const statusRaw = item.status as unknown;
      let status: number | undefined;
      if (typeof statusRaw === 'number') {
        status = statusRaw;
      } else if (typeof statusRaw === 'string') {
        const key = statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
        status = SubscriptionStatus[key] as number | undefined;
      }

      const is_active_raw = item.is_active as
        | boolean
        | number
        | string
        | undefined;
      const is_active =
        typeof status === 'number'
          ? status === SubscriptionStatus.ACTIVE
          : typeof is_active_raw === 'boolean'
            ? is_active_raw
            : Boolean(is_active_raw ?? true);

      const updated_at =
        (item.updated_at as string | undefined) ||
        (item.updatedAt as string | undefined) ||
        new Date().toISOString();

      // subscription_type may come as number or string (e.g., "BOATER")
      const subTypeRaw =
        (item.subscription_type as unknown) ||
        (item.subscriptionType as unknown) ||
        (item.type as unknown);
      let subscription_type: number | undefined = undefined;
      if (typeof subTypeRaw === 'number') {
        subscription_type = subTypeRaw;
      } else if (typeof subTypeRaw === 'string') {
        const asNum = Number(subTypeRaw);
        if (!Number.isNaN(asNum)) {
          subscription_type = asNum;
        } else {
          const key = subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
          if (key in SubscriptionType) {
            subscription_type = SubscriptionType[key] as number;
          }
        }
      }

      const is_most_popular = Boolean(item.is_most_popular ?? false);

      // Pass through translations map if the API returns it
      const translationsRaw = item.translations as unknown;
      let translations: ISubscription['translations'] | undefined = undefined;
      if (
        translationsRaw &&
        typeof translationsRaw === 'object' &&
        !Array.isArray(translationsRaw)
      ) {
        translations = translationsRaw as ISubscription['translations'];
      } else if (typeof translationsRaw === 'string') {
        try {
          translations = JSON.parse(translationsRaw) as ISubscription['translations'];
        } catch {
          translations = undefined;
        }
      }

      return {
        id,
        name,
        name_sub_title,
        description,
        price,
        billing_cycle,
        is_active,
        status,
        subscription_type,
        updated_at,
        is_most_popular,
        translations,
      };
    };

    const data = res.data as unknown;
    if (Array.isArray(data)) {
      return {
        subscriptions: data.map(normalizeItem),
        totalCount: data.length,
      };
    }

    const obj = (data ?? {}) as Record<string, unknown>;
    const status = obj.status as boolean | undefined;
    const inner = obj.data as unknown;
    if (status && inner) {
      if (Array.isArray(inner)) {
        return {
          subscriptions: inner.map(normalizeItem),
          totalCount: inner.length,
        };
      }
      const innerObj = (inner ?? {}) as Record<string, unknown>;
      const subs = innerObj.subscriptions as unknown[] | undefined;
      if (Array.isArray(subs)) {
        const totalCount =
          (innerObj.totalCount as number | undefined) ||
          (innerObj.totalItems as number | undefined) ||
          subs.length;
        return {
          subscriptions: subs.map(normalizeItem),
          totalCount,
        };
      }
    }

    // Fallback
    return {
      subscriptions: [],
      totalCount: 0,
    };
  },

  // Fetch current subscription plan for a club
  getCurrentClubPlan: async (clubId: string) => {
    try {
      const res = await ApiClient.get(
        `/subscriptions/clubs/${clubId}/current-plan`,
        {
          headers: getRequiredHeaders(),
        }
      );

      const data = res.data as unknown;
      const obj = (data ?? {}) as Record<string, unknown>;

      // Handle response structure: { status, message, data: { subscription, transaction_id, purchased_at } }
      if (obj.status && obj.data) {
        const innerData = obj.data as Record<string, unknown>;
        const subscription = innerData.subscription as Record<
          string,
          unknown
        > | null;

        if (!subscription) {
          return null;
        }

        // Normalize the subscription data
        const statusRaw = subscription.status as unknown;
        let status: number | undefined;
        if (typeof statusRaw === 'number') {
          status = statusRaw;
        } else if (typeof statusRaw === 'string') {
          const key =
            statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
          status = SubscriptionStatus[key] as number | undefined;
        }

        let billing_cycle: number | undefined = undefined;
        const bcRaw = subscription.billing_cycle as unknown;
        if (typeof bcRaw === 'number') {
          billing_cycle = bcRaw;
        } else if (typeof bcRaw === 'string') {
          const parsed = Number(bcRaw);
          if (!Number.isNaN(parsed)) {
            billing_cycle = parsed;
          } else {
            const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
            if (key in BillingCycle) {
              billing_cycle = BillingCycle[key] as number;
            }
          }
        }

        const subTypeRaw = subscription.subscription_type as unknown;
        let subscription_type: number | undefined = undefined;
        if (typeof subTypeRaw === 'number') {
          subscription_type = subTypeRaw;
        } else if (typeof subTypeRaw === 'string') {
          const asNum = Number(subTypeRaw);
          if (!Number.isNaN(asNum)) {
            subscription_type = asNum;
          } else {
            const key =
              subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
            if (key in SubscriptionType) {
              subscription_type = SubscriptionType[key] as number;
            }
          }
        }

        return {
          subscription: {
            id: (subscription.id as string) ?? '',
            name: (subscription.name as string) ?? '',
            name_sub_title: (subscription.name_sub_title as string) ?? '',
            description: (subscription.description as string) ?? '',
            price:
              typeof subscription.price === 'number'
                ? subscription.price
                : subscription.price !== undefined
                  ? Number(subscription.price)
                  : undefined,
            billing_cycle,
            is_active: status === SubscriptionStatus.ACTIVE,
            status,
            subscription_type,
            updated_at:
              (subscription.updated_at as string) ?? new Date().toISOString(),
            language: (subscription.language as string) ?? 'en',
            translations: subscription.translations as
              | Record<
                  string,
                  Partial<{
                    name: string;
                    description: string;
                    name_sub_title: string;
                  }>
                >
              | undefined,
          },
          transaction_id: (innerData.transaction_id as string) ?? '',
          purchased_at: (innerData.purchased_at as string) ?? '',
          renewal_date: (innerData.renewal_date as string | null) ?? null,
          cancellation_requested_at:
            (innerData.cancellation_requested_at as string | null) ?? null,
          subscription_end_date:
            (innerData.subscription_end_date as string | null) ?? null,
          auto_renew: (innerData.auto_renew as boolean | undefined) ?? false,
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching current club plan:', error);
      return null;
    }
  },

  // Fetch current subscription plan for a harbor
  getCurrentHarborPlan: async (harborId: string) => {
    try {
      const res = await ApiClient.get(
        `/subscriptions/harbors/${harborId}/current-plan`,
        {
          headers: getRequiredHeaders(),
        }
      );

      const data = res.data as unknown;
      const obj = (data ?? {}) as Record<string, unknown>;

      // Handle response structure: { status, message, data: { subscription, transaction_id, purchased_at } }
      if (obj.status && obj.data) {
        const innerData = obj.data as Record<string, unknown>;
        const subscription = innerData.subscription as Record<
          string,
          unknown
        > | null;

        if (!subscription) {
          return null;
        }

        // Normalize the subscription data
        const statusRaw = subscription.status as unknown;
        let status: number | undefined;
        if (typeof statusRaw === 'number') {
          status = statusRaw;
        } else if (typeof statusRaw === 'string') {
          const key =
            statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
          status = SubscriptionStatus[key] as number | undefined;
        }

        let billing_cycle: number | undefined = undefined;
        const bcRaw = subscription.billing_cycle as unknown;
        if (typeof bcRaw === 'number') {
          billing_cycle = bcRaw;
        } else if (typeof bcRaw === 'string') {
          const parsed = Number(bcRaw);
          if (!Number.isNaN(parsed)) {
            billing_cycle = parsed;
          } else {
            const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
            if (key in BillingCycle) {
              billing_cycle = BillingCycle[key] as number;
            }
          }
        }

        const subTypeRaw = subscription.subscription_type as unknown;
        let subscription_type: number | undefined = undefined;
        if (typeof subTypeRaw === 'number') {
          subscription_type = subTypeRaw;
        } else if (typeof subTypeRaw === 'string') {
          const asNum = Number(subTypeRaw);
          if (!Number.isNaN(asNum)) {
            subscription_type = asNum;
          } else {
            const key =
              subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
            if (key in SubscriptionType) {
              subscription_type = SubscriptionType[key] as number;
            }
          }
        }

        return {
          subscription: {
            id: (subscription.id as string) ?? '',
            name: (subscription.name as string) ?? '',
            name_sub_title: (subscription.name_sub_title as string) ?? '',
            description: (subscription.description as string) ?? '',
            price:
              typeof subscription.price === 'number'
                ? subscription.price
                : subscription.price !== undefined
                  ? Number(subscription.price)
                  : undefined,
            billing_cycle,
            is_active: status === SubscriptionStatus.ACTIVE,
            status,
            subscription_type,
            updated_at:
              (subscription.updated_at as string) ?? new Date().toISOString(),
            language: (subscription.language as string) ?? 'en',
            translations: subscription.translations as
              | Record<
                  string,
                  Partial<{
                    name: string;
                    description: string;
                    name_sub_title: string;
                  }>
                >
              | undefined,
          },
          transaction_id: (innerData.transaction_id as string) ?? '',
          purchased_at: (innerData.purchased_at as string) ?? '',
          associated_clubs: (innerData.associated_clubs as string[]) ?? [],
          message: (obj.message as string) ?? '',
          renewal_date: (innerData.renewal_date as string | null) ?? null,
          cancellation_requested_at:
            (innerData.cancellation_requested_at as string | null) ?? null,
          subscription_end_date:
            (innerData.subscription_end_date as string | null) ?? null,
          auto_renew: (innerData.auto_renew as boolean | undefined) ?? false,
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching current harbor plan:', error);
      return null;
    }
  },

  // Fetch current subscription plan for a service provider
  getCurrentServiceProviderPlan: async (serviceProviderId: string) => {
    try {
      const res = await ApiClient.get(
        `/subscriptions/service-providers/${serviceProviderId}/current-plan`,
        {
          headers: getRequiredHeaders(),
        }
      );

      const data = res.data as unknown;
      const obj = (data ?? {}) as Record<string, unknown>;

      // Handle response structure: { status, message, data: { subscription, transaction_id, purchased_at } }
      if (obj.status && obj.data) {
        const innerData = obj.data as Record<string, unknown>;
        const subscription = innerData.subscription as Record<
          string,
          unknown
        > | null;

        if (!subscription) {
          return null;
        }

        // Normalize the subscription data
        const statusRaw = subscription.status as unknown;
        let status: number | undefined;
        if (typeof statusRaw === 'number') {
          status = statusRaw;
        } else if (typeof statusRaw === 'string') {
          const key =
            statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
          status = SubscriptionStatus[key] as number | undefined;
        }

        let billing_cycle: number | undefined = undefined;
        const bcRaw = subscription.billing_cycle as unknown;
        if (typeof bcRaw === 'number') {
          billing_cycle = bcRaw;
        } else if (typeof bcRaw === 'string') {
          const parsed = Number(bcRaw);
          if (!Number.isNaN(parsed)) {
            billing_cycle = parsed;
          } else {
            const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
            if (key in BillingCycle) {
              billing_cycle = BillingCycle[key] as number;
            }
          }
        }

        const subTypeRaw = subscription.subscription_type as unknown;
        let subscription_type: number | undefined = undefined;
        if (typeof subTypeRaw === 'number') {
          subscription_type = subTypeRaw;
        } else if (typeof subTypeRaw === 'string') {
          const asNum = Number(subTypeRaw);
          if (!Number.isNaN(asNum)) {
            subscription_type = asNum;
          } else {
            const key =
              subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
            if (key in SubscriptionType) {
              subscription_type = SubscriptionType[key] as number;
            }
          }
        }

        return {
          subscription: {
            id: (subscription.id as string) ?? '',
            name: (subscription.name as string) ?? '',
            name_sub_title: (subscription.name_sub_title as string) ?? '',
            description: (subscription.description as string) ?? '',
            price:
              typeof subscription.price === 'number'
                ? subscription.price
                : subscription.price !== undefined
                  ? Number(subscription.price)
                  : undefined,
            billing_cycle,
            is_active: status === SubscriptionStatus.ACTIVE,
            status,
            subscription_type,
            updated_at:
              (subscription.updated_at as string) ?? new Date().toISOString(),
            language: (subscription.language as string) ?? 'en',
            translations: subscription.translations as
              | Record<
                  string,
                  Partial<{
                    name: string;
                    description: string;
                    name_sub_title: string;
                  }>
                >
              | undefined,
          },
          transaction_id: (innerData.transaction_id as string) ?? '',
          purchased_at: (innerData.purchased_at as string) ?? '',
          renewal_date: (innerData.renewal_date as string | null) ?? null,
          cancellation_requested_at:
            (innerData.cancellation_requested_at as string | null) ?? null,
          subscription_end_date:
            (innerData.subscription_end_date as string | null) ?? null,
          auto_renew: (innerData.auto_renew as boolean | undefined) ?? false,
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching current service provider plan:', error);
      return null;
    }
  },

  // Fetch current subscription plan for a DMO
  getCurrentDMOPlan: async (dmoId: string) => {
    try {
      const res = await ApiClient.get(
        `/subscriptions/dmo/${dmoId}/current-plan`,
        {
          headers: getRequiredHeaders(),
        }
      );

      const data = res.data as unknown;
      const obj = (data ?? {}) as Record<string, unknown>;

      // Handle response structure: { status, message, data: { subscription, transaction_id, purchased_at } }
      if (obj.status && obj.data) {
        const innerData = obj.data as Record<string, unknown>;
        const subscription = innerData.subscription as Record<
          string,
          unknown
        > | null;

        if (!subscription) {
          return null;
        }

        // Normalize the subscription data
        const statusRaw = subscription.status as unknown;
        let status: number | undefined;
        if (typeof statusRaw === 'number') {
          status = statusRaw;
        } else if (typeof statusRaw === 'string') {
          const key =
            statusRaw.toUpperCase() as keyof typeof SubscriptionStatus;
          status = SubscriptionStatus[key] as number | undefined;
        }

        let billing_cycle: number | undefined = undefined;
        const bcRaw = subscription.billing_cycle as unknown;
        if (typeof bcRaw === 'number') {
          billing_cycle = bcRaw;
        } else if (typeof bcRaw === 'string') {
          const parsed = Number(bcRaw);
          if (!Number.isNaN(parsed)) {
            billing_cycle = parsed;
          } else {
            const key = bcRaw.toUpperCase() as keyof typeof BillingCycle;
            if (key in BillingCycle) {
              billing_cycle = BillingCycle[key] as number;
            }
          }
        }

        const subTypeRaw = subscription.subscription_type as unknown;
        let subscription_type: number | undefined = undefined;
        if (typeof subTypeRaw === 'number') {
          subscription_type = subTypeRaw;
        } else if (typeof subTypeRaw === 'string') {
          const asNum = Number(subTypeRaw);
          if (!Number.isNaN(asNum)) {
            subscription_type = asNum;
          } else {
            const key =
              subTypeRaw.toUpperCase() as keyof typeof SubscriptionType;
            if (key in SubscriptionType) {
              subscription_type = SubscriptionType[key] as number;
            }
          }
        }

        return {
          subscription: {
            id: (subscription.id as string) ?? '',
            name: (subscription.name as string) ?? '',
            name_sub_title: (subscription.name_sub_title as string) ?? '',
            description: (subscription.description as string) ?? '',
            price:
              typeof subscription.price === 'number'
                ? subscription.price
                : subscription.price !== undefined
                  ? Number(subscription.price)
                  : undefined,
            billing_cycle,
            is_active: status === SubscriptionStatus.ACTIVE,
            status,
            subscription_type,
            updated_at:
              (subscription.updated_at as string) ?? new Date().toISOString(),
            language: (subscription.language as string) ?? 'en',
            translations: subscription.translations as
              | Record<
                  string,
                  Partial<{
                    name: string;
                    description: string;
                    name_sub_title: string;
                  }>
                >
              | undefined,
          },
          transaction_id: (innerData.transaction_id as string) ?? '',
          purchased_at: (innerData.purchased_at as string) ?? '',
          associated_clubs: (innerData.associated_clubs as string[]) ?? [],
          message: (obj.message as string) ?? '',
          renewal_date: (innerData.renewal_date as string | null) ?? null,
          cancellation_requested_at:
            (innerData.cancellation_requested_at as string | null) ?? null,
          subscription_end_date:
            (innerData.subscription_end_date as string | null) ?? null,
          auto_renew: (innerData.auto_renew as boolean | undefined) ?? false,
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching current DMO plan:', error);
      return null;
    }
  },

  cancelUserSubscriptionAutoRenew: async (userId: string) => {
    const res = await ApiClient.post(`/subscriptions/users/${userId}/cancel`, {}, { headers: getRequiredHeaders() });
    return res.data;
  },

  cancelHarborSubscriptionAutoRenew: async (harborId: string) => {
    const res = await ApiClient.post(`/subscriptions/harbors/${harborId}/cancel-auto-renew`, {}, { headers: getRequiredHeaders() });
    return res.data;
  },

  cancelClubSubscriptionAutoRenew: async (clubRegistrationRequestId: string) => {
    const res = await ApiClient.post(
      `/subscriptions/clubs/${clubRegistrationRequestId}/cancel-auto-renew`,
      {},
      { headers: getRequiredHeaders() }
    );
    return res.data;
  },

  cancelServiceProviderSubscriptionAutoRenew: async (serviceProviderId: string) => {
    const res = await ApiClient.post(
      `/subscriptions/service-providers/${serviceProviderId}/cancel-auto-renew`,
      {},
      { headers: getRequiredHeaders() }
    );
    return res.data;
  },

  cancelDmoSubscriptionAutoRenew: async (dmoRegistrationRequestId: string) => {
    const res = await ApiClient.post(
      `/subscriptions/dmo/${dmoRegistrationRequestId}/cancel-auto-renew`,
      {},
      { headers: getRequiredHeaders() }
    );
    return res.data;
  },
};
